import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type UserRole = 'SUPER_ADMIN' | 'SCHOOL_ADMIN' | 'EMPLOYEE' | string;

export interface User {
    user_id: string;
    email: string;
    name: string;
    role: string;
    school_id: string | null;
    is_super_admin: boolean;
    avatar?: string;
    user_metadata?: {
        email_verified?: boolean;
        full_name?: string;
        photo_filename?: string;
    };
    school?: {
        id: string;
        name: string;
        slug: string;
        admin_email?: string;
        logo_url?: string;
        banner_url?: string;
        primary_color?: string;
        accent_color?: string;
        tagline?: string;
        support_email?: string;
        support_phone?: string;
        created_at?: string;
        updated_at?: string;
    } | null;
    employee_details?: {
        id: string;
        user_id: string;
        department_id: string;
        level: number;
        departments: {
            id: string;
            name: string;
            created_at?: string;
        };
    } | null;
    parent_details?: any | null;
}

export interface TenantSettings {
    id?: string;
    name: string;
    slug?: string;
    logo_url?: string;
    banner_url?: string;
    primary_color?: string;
    accent_color?: string;
    tagline?: string;
    support_email?: string;
    support_phone?: string;
    created_at?: string;
    
    // Legacy mapping aliases
    logo?: string;
    primaryColor?: string;
    secondaryColor?: string;
    favicon?: string;
}

interface AuthState {
    user: User | null;
    tenant: TenantSettings | null;
    isAuthenticated: boolean;
    setAuth: (user: User) => void;
    setTenant: (tenant: TenantSettings) => void;
    logout: () => void;
    updateUser: (user: Partial<User>) => void;
}

/**
 * Auth Store
 * Manages user session, roles, and tenant context
 */
export const useAuthStore = create<AuthState>()(
    persist(
        (set) => ({
            user: null,
            tenant: null,
            isAuthenticated: false,

            setAuth: (user) => set((state) => ({
                user,
                isAuthenticated: true,
                // Hydrate tenant config from user.school if available, fallback to existing state
                tenant: user.school ? {
                    ...(state.tenant || {}),
                    ...user.school
                } : state.tenant
            })),
            setTenant: (tenant) => set({ tenant }),
            logout: () => {
                set({ user: null, tenant: null, isAuthenticated: false });
                localStorage.removeItem('raabta-auth-storage');
                localStorage.removeItem('auth_token');
                localStorage.removeItem('refresh_token');
            },
            updateUser: (userData) => set((state) => ({
                user: state.user ? { ...state.user, ...userData } : null
            })),
        }),
        {
            name: 'raabta-auth-storage',
        }
    )
);
